import { SelectOption } from "@touche-lts/panel-ui/dist";
import { makeAutoObservable } from "mobx";
import RootStore  from "mobx-stores/root-store";
import {
  AddressesDTO,
  CredicCardsDTO,
  ShippingBillingAddressDTO,
  ShippingGeoCountriesDTO,
  UserDTO,
} from "models";
import {
  AccountCurrencyPreference,
  AccountLangPreference,
} from "pages/account/account-details/account-details.types";
import { Api } from "services";
import { getSelectOptions } from "utilities";
import _ from "lodash";

export class AccountStore {
  private rootStore: any;
  private userApi: Api["userApi"];

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    this.userApi = this.rootStore.generalStore.api.userApi;
    makeAutoObservable(this);
  }

  // OBSERVABLES
  private accountStore: Partial<UserDTO> = null;
  private password: string = null;
  private newPassword: string = null;
  private repeatNewPassword: string = null;
  private creditCards: CredicCardsDTO[] = null;
  private selectedAccountLangPreference: AccountLangPreference =
    AccountLangPreference.ENGLISH;
  private selectedAccountCurrencyPreference: AccountCurrencyPreference =
    AccountCurrencyPreference.USD;
  private geoCountriesBillingShipping: ShippingGeoCountriesDTO[] = [];
  private userAddresses: AddressesDTO[] = null;
  private addEditAddressBox: boolean = false;
  private userId = JSON.parse(localStorage.getItem("userDetails"))?.id;
  private sameAsBilling: boolean = false;
  private addEditBillingShippingAddress: Partial<AddressesDTO> = {
    id: null,
    name: "",
    shippingAddress: {
      country: "",
      city: "",
      address: "",
      zipcode: "",
      id: null,
    },
    billingAddress: {
      country: "",
      city: "",
      address: "",
      zipcode: "",
      id: null,
    },
    primary: false,
  };

  // ACTIONS

  public setBilling = (
    billingDetails: Partial<ShippingBillingAddressDTO>
  ): void => {
    this.addEditBillingShippingAddress.billingAddress = {
      ...this.addEditBillingShippingAddress.billingAddress,
      ...billingDetails,
    };
  };

  public setShipping = (
    shippingDetails: Partial<ShippingBillingAddressDTO>
  ): void => {
    this.addEditBillingShippingAddress.shippingAddress = {
      ...this.addEditBillingShippingAddress.shippingAddress,
      ...shippingDetails,
    };
  };

  public setBillingShippingNamePrimary = (
    BSDetails: Partial<AddressesDTO>
  ): void => {
    this.addEditBillingShippingAddress = {
      ...this.addEditBillingShippingAddress,
      ...BSDetails,
    };
  };

  public setAccountStore = (accountDetails: Partial<UserDTO>): void => {
    this.accountStore = accountDetails;
  };

  public setAccountStoreProps = (props: Partial<UserDTO>): void => {
    this.setAccountStore({ ...this.accountStore, ...props });
  };

  public passwordChange = (passChange: string): void => {
    this.password = passChange;
  };

  public newPasswordChange = (passChange: string): void => {
    this.newPassword = passChange;
  };
  
  public repeatNewPasswordChange = (passChange: string): void => {
    this.repeatNewPassword = passChange;
  };

  public resetPasswordBoxes = (): void => {
    this.password = null;
    this.newPassword = null;
    this.repeatNewPassword = null;
  };

  public setLangPreference = (language: AccountLangPreference): void => {
    this.selectedAccountLangPreference = language;
    this.setAccountStoreProps({ language: language.toLocaleUpperCase() });
  };

  public setCurrencyPreference = (
    currency: AccountCurrencyPreference
  ): void => {
    this.selectedAccountCurrencyPreference = currency;
    this.setAccountStoreProps({ currency: currency });
  };

  public setSelectedLangPrefference = (lang: string): void => {
    this.selectedAccountLangPreference =
      AccountLangPreference[lang.toUpperCase()];
  };

  public setSelectedCurrencyPrefference = (currency: string): void => {
    this.selectedAccountCurrencyPreference =
      AccountCurrencyPreference[currency.toUpperCase()];
  };

  public setLangAndCurrency = (lang: string, currency: string) => {
    this.setSelectedLangPrefference(lang);
    this.setSelectedCurrencyPrefference(currency);
  };

  public setAddEditBox = (boxState: boolean): void => {
    this.addEditAddressBox = boxState;
  };

  public discardChanges = (): void => {
    this.addEditAddressBox = false;
    this.resetBillingAddressForm();
    this.setSameAsBilling(false);
  };

  public setSameAsBilling = (billing: boolean): void => {
    this.sameAsBilling = billing;
  };

  // COMPUTED
  public get accountDetails() {
    // TODO-A put correct model
    return this.accountStore;
  }

  public get userPassword(): string {
    return this.password;
  }

  public get newUserPassword(): string {
    return this.newPassword;
  }

  public get repeatNewUserPassword(): string {
    return this.repeatNewPassword;
  }

  public get creditCardsDetails(): CredicCardsDTO[] {
    return this.creditCards;
  }

  public get accountLangPreferenceSelected(): AccountLangPreference {
    return this.selectedAccountLangPreference;
  }

  public get accountLangPreferenceOptions(): SelectOption<AccountLangPreference>[] {
    return getSelectOptions(AccountLangPreference);
  }

  public get accountCurrencyPreferenceSelected(): AccountCurrencyPreference {
    return this.selectedAccountCurrencyPreference;
  }

  public get addressShippingBillingCountryOptions(): any {
    return this.geoCountriesBillingShipping.map(
      (address: ShippingGeoCountriesDTO) => {
        return {
          label: address.name,
          value: address.name,
        };
      }
    );
  }

  public get billingShippingInfo() {
    return this.addEditBillingShippingAddress;
  }

  public get accountCurrencyPreferenceOptions(): SelectOption<AccountCurrencyPreference>[] {
    return getSelectOptions(AccountCurrencyPreference);
  }

  public get userAddressesInfo(): AddressesDTO[] {
    return this.userAddresses;
  }

  public get addEditBoxState(): boolean {
    return this.addEditAddressBox;
  }

  public get sameAsBillingCheck(): boolean {
    return this.sameAsBilling;
  }

  // METHODS
  public getUserById = async (userId: number): Promise<UserDTO> => {
    const userDetails = await this.userApi.getUser(userId);
    if (!userDetails) return;
    this.accountStore = userDetails;
    this.setLangAndCurrency(userDetails?.language, userDetails?.currency);
  };

  public updateUser = async (
    userId: number,
    userUpdatePassword?: boolean,
    imageUploadUrl?: string
  ): Promise<UserDTO> => {
    let payloadDetails = {};
    if (!userUpdatePassword) {
        if (imageUploadUrl) {
        payloadDetails = { ...this.accountDetails, imageUrl: imageUploadUrl };
      } else {
        payloadDetails = { ...this.accountDetails };
      }
    }
    const payload = {
      ...payloadDetails,
      ...{ password: this.password, newPassword: this.newPassword },
    };
    if(userUpdatePassword){
      await this.userApi.updateUserPassword(payload);
      this.resetPasswordBoxes();
      return;
    }
    const userDetails = await this.userApi.updateUser(userId,payload);
    if (!userDetails) {
      return;
    }
    this.setAccountStore(userDetails);
    this.setLangAndCurrency(userDetails?.language, userDetails?.currency);
    this.resetPasswordBoxes();
  };

  public getCreditCards = async (userId: number): Promise<CredicCardsDTO[]> => {
    const creditCardDetails = await this.userApi.getCreditCards(userId);
    if (!creditCardDetails) return this.creditCards = null;
    this.creditCards = creditCardDetails;
  };

  public addCreditCard = async (userId: number, creditCard: CredicCardsDTO) => {
    await this.userApi.addCreditCard(userId, creditCard);
    this.getCreditCards(userId);
  };

  public deleteCreditCard = async (
    userId: number | string,
    creditCardId: number | string
  ) => {
    await this.userApi.deleteCreditCard(userId, creditCardId);
    this.getCreditCards(this.userId);
  };

  public getUserAddressesById = async (
    userId: number | string
  ): Promise<UserDTO> => {
    const userAddresses = await this.userApi.getUserAddresses(userId);
    if (!userAddresses) return;
    this.userAddresses = userAddresses;
  };

  public addUserAddress = async (userId: number | string) => {
    if (!this.checkShippingAddressPopulated()) return;
    if (this.sameAsBilling) {
      this.addEditBillingShippingAddress.billingAddress = _.cloneDeep(
        this.addEditBillingShippingAddress.shippingAddress
      );
    }
    const userAddress = await this.userApi.addUserAddress(
      userId,
      this.addEditBillingShippingAddress
    );

    if (!userAddress) return;
    this.discardChanges();
    this.getUserAddressesById(userId);
  };

  public updateUserAddress = async (userId: number | string) => {
    if (!this.checkShippingAddressPopulated()) return;
    if (this.sameAsBilling) {
      this.addEditBillingShippingAddress.billingAddress = _.cloneDeep(
        this.addEditBillingShippingAddress.shippingAddress
      );
    }
    const userAddress = await this.userApi.updateUserAddress(
      userId,
      this.addEditBillingShippingAddress.id,
      this.addEditBillingShippingAddress
    );
    if (!userAddress) return;
    this.discardChanges();
    this.getUserAddressesById(userId);
  };

  public editUserAddress = (address: AddressesDTO): void => {
    this.addEditBillingShippingAddress = {
      ...this.addEditBillingShippingAddress,
      ..._.cloneDeep(address),
    };
    this.addEditAddressBox = true;
  };

  public getGeoShippingCountries = async (): Promise<
    ShippingGeoCountriesDTO[]
  > => {
    const geoCountries = await this.userApi.getAddressShippingCountries();
    if (!geoCountries) return;
    this.geoCountriesBillingShipping = geoCountries;
  };

  public makePrimary = (address: AddressesDTO): void => {
    this.addEditBillingShippingAddress = {
      ...this.addEditBillingShippingAddress,
      ..._.cloneDeep(address),
    };
    this.addEditBillingShippingAddress.primary = true;
    this.updateUserAddress(this.userId);
  };

  public deleteAdress = async (addressId: number) => {
    const deletedAddress = await this.userApi.deleteUserAddress(
      this.userId,
      addressId
    );
    if (!deletedAddress) return;
    this.discardChanges();
    this.getUserAddressesById(this.userId);
  };

  public resetBillingAddressForm(): void {
    this.addEditBillingShippingAddress = {
      name: "",
      shippingAddress: {
        country: "",
        city: "",
        address: "",
        zipcode: "",
      },
      billingAddress: {
        country: "",
        city: "",
        address: "",
        zipcode: "",
      },
      primary: false,
    };
  }

  private checkAllPopulated = (): boolean => {
    const addressDTO = _.cloneDeep(this.addEditBillingShippingAddress);
    delete addressDTO.shippingAddress.id;
    delete addressDTO.billingAddress.id;
    if (
      addressDTO.name === "" ||
      _.values(addressDTO.shippingAddress).some(_.isEmpty || "") ||
      _.values(addressDTO.billingAddress).some(_.isEmpty || "")
    ) {
      return false;
    }
    return true;
  };

  private checkShippingAddressPopulated = (): boolean => {
    const addressDTO = _.cloneDeep(this.addEditBillingShippingAddress);
    delete addressDTO.shippingAddress.id;
    delete addressDTO.billingAddress.id;
    if (
      addressDTO.name === "" ||
      _.values(addressDTO.shippingAddress).some(_.isEmpty || "") 
    ) {
      return false;
    }
    return true;
  };
}
